'use client';


import { useLicense } from '@/hooks/useLicense';
import { withRef } from '@udecode/cn';
import { useEditorRef } from '@udecode/plate/react';
import { SparklesIcon } from 'lucide-react';
import { ToolbarButton } from './toolbar';
import { useDialog } from '@/hooks/useDialog';
import { useNavigate, useParams } from 'react-router-dom';
import { CopilotPlugin } from '@udecode/plate-ai/react';
import { updateChapterTitle } from '@/services/supabase/chapters';
import { toast } from 'sonner';

export const CopilotToolbarButton = withRef<typeof ToolbarButton>(
    ({ children, ...rest }, ref) => {
        const editor = useEditorRef();
        const { hasAIFeatures } = useLicense();

        // Don't render if user doesn't have AI features
        if (!hasAIFeatures) {
            return null;
        }

        const { openDialog } = useDialog();
        const navigate = useNavigate();
        const { projectId } = useParams();

        const handleClick = async () => {
            // @ts-ignore
            const copilotApi = editor.api.copilot;

            if (copilotApi && copilotApi.triggerSuggestion) {
                // Validation Check: Ensure there's enough context
                const options = editor.getOptions(CopilotPlugin) as any;
                const { chapterBeats = [], chapterDescription = '', chapterId, chapterTitle } = options || {};
                
                const beatsCount = chapterBeats?.length || 0;
                const hasOutline = !!chapterDescription && chapterDescription !== '-' && chapterDescription.trim().length > 0;

                if (beatsCount < 2 && !hasOutline) {
                    openDialog('copilot-warning', {
                        projectId,
                        chapterId,
                        beatsCount,
                        hasOutline,
                        onPlanChapter: () => {
                            navigate(`/projects/${projectId}/chapters/plan-scenes`);
                        },
                        onWriteOutline: () => {
                           openDialog('form', {
                               title: "Chapter Outline",
                               description: "Write a high-level outline to give the AI context about what happens in this chapter.",
                               fields: [
                                   { 
                                       name: "description", 
                                       label: "Outline", 
                                       type: "textarea", 
                                       placeholder: "What happens in this chapter? Who is involved?",
                                       required: true,
                                       defaultValue: chapterDescription === '-' ? '' : chapterDescription
                                   }
                               ],
                               submitText: "Save Outline"
                           }, async (data: { description: string }) => {
                               try {
                                   if (!chapterId) return;
                                   await updateChapterTitle(Number(chapterId), chapterTitle, data.description);
                                   toast.success("Outline saved", { description: "You can now try suggesting again with better context!" });
                                   
                                   // Refresh editor options locally
                                   if (options) {
                                       options.chapterDescription = data.description;
                                   }
                               } catch (error) {
                                   toast.error("Failed to save outline");
                               }
                           });
                        }
                    });
                    return;
                }

                try {
                    // Safe-guard against empty selections if editor wasn't focused
                    if (!editor.selection) {
                        editor.tf.focus();
                        const lastBlockIndex = editor.children.length - 1;
                        if (lastBlockIndex >= 0) {
                            editor.tf.select([lastBlockIndex]);
                            editor.tf.collapse({ edge: 'end' });
                        }
                    }

                    setTimeout(() => {
                        copilotApi.triggerSuggestion();
                    }, 10);
                } catch (error) {
                    console.error('Error triggering Copilot suggestion:', error);
                }
            }
        };

        return (
            <ToolbarButton
                ref={ref}
                tooltip="Copilot Suggestion"
                {...rest}
                onClick={handleClick}
                onMouseDown={(e) => {
                    e.preventDefault();
                }}
            >
                <SparklesIcon />
            </ToolbarButton>
        );
    }
);
